// =============================================================================
// Admin Deal Detail Page
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { XMarkIcon, TrashIcon, PencilIcon, ExclamationTriangleIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, CloudArrowUpIcon, DocumentTextIcon, ShieldExclamationIcon, Bars2Icon, MapPinIcon } from '@heroicons/react/24/outline';
import { adminService, type DealWithStats, type UpdateDealInput, type InvestorWithProfiles, type ShareClass, type CreateShareClassInput, type UpdateShareClassInput } from '../../services/admin.service';
import { Button, DocumentViewerModal, DealImage, PercentInput, CurrencyInput, IntegerInput } from '../../components/ui';
import WaterfallTab from '../../components/admin/WaterfallTab';
import { getApiError } from '../../services/api';
import type { PropertyType } from '../../types';
import { compressImage, validateImageFile } from '../../utils/imageCompression';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface DealDetails extends DealWithStats {
  imageUrl?: string;
  investments: {
    id: string;
    investorId: string;
    investorName: string;
    investorEmail: string;
    profileId: string;
    profileName: string;
    investmentAmount: number;
    ownershipPercentage: number;
    totalDistributed: number;
    isActive: boolean;
    isPromoteRecipient?: boolean;
    investorRole?: 'LP' | 'GP';
    investmentDate?: string;
    shareClassId?: string | null;
    shareClassName?: string | null;
    shareClassCode?: string | null;
    contributions?: {
      id: string;
      amount: number;
      fundingDate: string;
      notes?: string;
    }[];
  }[];
  distributions: {
    id: string;
    type: string;
    totalAmount: number;
    distributionDate: string;
    paymentMethod: string;
    status: 'DRAFT' | 'PUBLISHED';
    notes?: string;
  }[];
  documents: { id: string; type: string; name: string; createdAt: string }[];
}

// =============================================================================
// Edit Deal Modal
// =============================================================================

interface EditDealModalProps {
  isOpen: boolean;
  deal: DealDetails;
  onClose: () => void;
  onSaved: () => void;
}

function EditDealModal({ isOpen, deal, onClose, onSaved }: EditDealModalProps) {
  const [formData, setFormData] = useState<UpdateDealInput>({
    name: deal.name,
    address: deal.address,
    city: deal.city,
    state: deal.state,
    zipCode: deal.zipCode,
    propertyType: deal.propertyType as PropertyType,
    totalEquityRaised: deal.totalEquityRaised,
    acquisitionDate: deal.acquisitionDate?.split('T')[0] || '',
    description: deal.description || '',
    isActive: deal.isActive,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: deal.name,
        address: deal.address,
        city: deal.city,
        state: deal.state,
        zipCode: deal.zipCode,
        propertyType: deal.propertyType as PropertyType,
        totalEquityRaised: deal.totalEquityRaised,
        acquisitionDate: deal.acquisitionDate?.split('T')[0] || '',
        description: deal.description || '',
        isActive: deal.isActive,
      });
      setError(null);
    }
  }, [isOpen, deal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.updateDeal(deal.id, formData);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const propertyTypes: PropertyType[] = [
    'MULTIFAMILY',
    'RETAIL',
    'OFFICE',
    'INDUSTRIAL',
    'MIXED_USE',
    'HOTEL',
    'SELF_STORAGE',
    'OTHER',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Deal</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Name *</label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Type *</label>
            <select
              value={formData.propertyType || ''}
              onChange={(e) => setFormData({ ...formData, propertyType: e.target.value as PropertyType })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {propertyTypes.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
              <input
                type="text"
                value={formData.zipCode || ''}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                required
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Equity Raised *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.totalEquityRaised ? formData.totalEquityRaised.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, totalEquityRaised: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="5,000,000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acquisition Date</label>
            <input
              type="date"
              value={formData.acquisitionDate || ''}
              onChange={(e) => setFormData({ ...formData, acquisitionDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive ?? true}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Deal is Active
              <span className="block text-xs text-gray-500 font-normal">
                Inactive deals are hidden from investors
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Deal Terms Modal
// =============================================================================

interface FundTermsFormData {
  // Deal Term and Extensions
  fundFormationDate: string;
  investmentPeriodStart: string;
  investmentPeriodEnd: string;
  fundTermYears: number | null;
  fundTermEndDate: string;
  extensionYears: number | null;
  extensionsUsed: number;
  windDownPeriodMonths: number | null;
  // Fee Structure
  managementFeeRate: number | null;
  managementFeeBasis: string;
  managementFeeStartDate: string;
  managementFeeReducedRate: number | null;
  acquisitionFeeRate: number | null;
  dispositionFeeRate: number | null;
  assetManagementFeeRate: number | null;
  feeOffsetEnabled: boolean;
  feeOffsetPercent: number | null;
  orgExpensesCap: number | null;
  orgExpensesAmortized: boolean;
  // Capital Management
  totalCommittedCapital: number | null;
  totalCalledCapital: number | null;
  recyclingEnabled: boolean;
  recyclingPeriodEnd: string;
  recyclingLimit: number | null;
  // Clawback & Lookback
  clawbackEnabled: boolean;
  lookbackEnabled: boolean;
  // Escrow
  escrowEnabled: boolean;
  escrowPercent: number | null;
  // Waterfall
  waterfallType: 'AMERICAN' | 'EUROPEAN';
  // Key Person
  keyPersonsEnabled: boolean;
  keyPersonNames: string[];
  keyPersonTriggered: boolean;
  keyPersonTriggerDate: string;
}

interface EditFundTermsModalProps {
  isOpen: boolean;
  deal: DealDetails;
  onClose: () => void;
  onSaved: () => void;
}

function EditFundTermsModal({ isOpen, deal, onClose, onSaved }: EditFundTermsModalProps) {
  const dealAny = deal as any;
  const [formData, setFormData] = useState<FundTermsFormData>({
    fundFormationDate: dealAny.fundFormationDate?.split('T')[0] || '',
    investmentPeriodStart: dealAny.investmentPeriodStart?.split('T')[0] || '',
    investmentPeriodEnd: dealAny.investmentPeriodEnd?.split('T')[0] || '',
    fundTermYears: dealAny.fundTermYears || null,
    fundTermEndDate: dealAny.fundTermEndDate?.split('T')[0] || '',
    extensionYears: dealAny.extensionYears || null,
    extensionsUsed: dealAny.extensionsUsed || 0,
    windDownPeriodMonths: dealAny.windDownPeriodMonths || null,
    managementFeeRate: dealAny.managementFeeRate != null ? dealAny.managementFeeRate * 100 : null,
    managementFeeBasis: dealAny.managementFeeBasis || 'COMMITTED_CAPITAL',
    managementFeeStartDate: dealAny.managementFeeStartDate?.split('T')[0] || '',
    managementFeeReducedRate: dealAny.managementFeeReducedRate != null ? dealAny.managementFeeReducedRate * 100 : null,
    acquisitionFeeRate: dealAny.acquisitionFeeRate != null ? dealAny.acquisitionFeeRate * 100 : null,
    dispositionFeeRate: dealAny.dispositionFeeRate != null ? dealAny.dispositionFeeRate * 100 : null,
    assetManagementFeeRate: dealAny.assetManagementFeeRate != null ? dealAny.assetManagementFeeRate * 100 : null,
    feeOffsetEnabled: dealAny.feeOffsetEnabled || false,
    feeOffsetPercent: dealAny.feeOffsetPercent != null ? dealAny.feeOffsetPercent * 100 : null,
    orgExpensesCap: dealAny.orgExpensesCap || null,
    orgExpensesAmortized: dealAny.orgExpensesAmortized || false,
    totalCommittedCapital: dealAny.totalCommittedCapital || null,
    totalCalledCapital: dealAny.totalCalledCapital || null,
    recyclingEnabled: dealAny.recyclingEnabled || false,
    recyclingPeriodEnd: dealAny.recyclingPeriodEnd?.split('T')[0] || '',
    recyclingLimit: dealAny.recyclingLimit != null ? dealAny.recyclingLimit * 100 : null,
    clawbackEnabled: dealAny.clawbackEnabled ?? true,
    lookbackEnabled: dealAny.lookbackEnabled || false,
    escrowEnabled: dealAny.escrowEnabled || false,
    escrowPercent: dealAny.escrowPercent != null ? dealAny.escrowPercent * 100 : null,
    waterfallType: dealAny.waterfallType || 'AMERICAN',
    keyPersonsEnabled: dealAny.keyPersonsEnabled || false,
    keyPersonNames: dealAny.keyPersonNames || [],
    keyPersonTriggered: dealAny.keyPersonTriggered || false,
    keyPersonTriggerDate: dealAny.keyPersonTriggerDate?.split('T')[0] || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'terms' | 'fees' | 'capital' | 'clawback' | 'keyperson'>('terms');
  const [keyPersonInput, setKeyPersonInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      const d = deal as any;
      setFormData({
        fundFormationDate: d.fundFormationDate?.split('T')[0] || '',
        investmentPeriodStart: d.investmentPeriodStart?.split('T')[0] || '',
        investmentPeriodEnd: d.investmentPeriodEnd?.split('T')[0] || '',
        fundTermYears: d.fundTermYears || null,
        fundTermEndDate: d.fundTermEndDate?.split('T')[0] || '',
        extensionYears: d.extensionYears || null,
        extensionsUsed: d.extensionsUsed || 0,
        windDownPeriodMonths: d.windDownPeriodMonths || null,
        managementFeeRate: d.managementFeeRate != null ? d.managementFeeRate * 100 : null,
        managementFeeBasis: d.managementFeeBasis || 'COMMITTED_CAPITAL',
        managementFeeStartDate: d.managementFeeStartDate?.split('T')[0] || '',
        managementFeeReducedRate: d.managementFeeReducedRate != null ? d.managementFeeReducedRate * 100 : null,
        acquisitionFeeRate: d.acquisitionFeeRate != null ? d.acquisitionFeeRate * 100 : null,
        dispositionFeeRate: d.dispositionFeeRate != null ? d.dispositionFeeRate * 100 : null,
        assetManagementFeeRate: d.assetManagementFeeRate != null ? d.assetManagementFeeRate * 100 : null,
        feeOffsetEnabled: d.feeOffsetEnabled || false,
        feeOffsetPercent: d.feeOffsetPercent != null ? d.feeOffsetPercent * 100 : null,
        orgExpensesCap: d.orgExpensesCap || null,
        orgExpensesAmortized: d.orgExpensesAmortized || false,
        totalCommittedCapital: d.totalCommittedCapital || null,
        totalCalledCapital: d.totalCalledCapital || null,
        recyclingEnabled: d.recyclingEnabled || false,
        recyclingPeriodEnd: d.recyclingPeriodEnd?.split('T')[0] || '',
        recyclingLimit: d.recyclingLimit != null ? d.recyclingLimit * 100 : null,
        clawbackEnabled: d.clawbackEnabled ?? true,
        lookbackEnabled: d.lookbackEnabled || false,
        escrowEnabled: d.escrowEnabled || false,
        escrowPercent: d.escrowPercent != null ? d.escrowPercent * 100 : null,
        waterfallType: d.waterfallType || 'AMERICAN',
        keyPersonsEnabled: d.keyPersonsEnabled || false,
        keyPersonNames: d.keyPersonNames || [],
        keyPersonTriggered: d.keyPersonTriggered || false,
        keyPersonTriggerDate: d.keyPersonTriggerDate?.split('T')[0] || '',
      });
      setError(null);
      setActiveSection('terms');
    }
  }, [isOpen, deal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Convert percentages back to decimals and prepare payload
      const payload = {
        fundFormationDate: formData.fundFormationDate || undefined,
        investmentPeriodStart: formData.investmentPeriodStart || undefined,
        investmentPeriodEnd: formData.investmentPeriodEnd || undefined,
        fundTermYears: formData.fundTermYears || undefined,
        fundTermEndDate: formData.fundTermEndDate || undefined,
        extensionYears: formData.extensionYears || undefined,
        extensionsUsed: formData.extensionsUsed,
        windDownPeriodMonths: formData.windDownPeriodMonths || undefined,
        managementFeeRate: formData.managementFeeRate != null ? formData.managementFeeRate / 100 : null,
        managementFeeBasis: formData.managementFeeBasis || undefined,
        managementFeeStartDate: formData.managementFeeStartDate || undefined,
        managementFeeReducedRate: formData.managementFeeReducedRate != null ? formData.managementFeeReducedRate / 100 : null,
        acquisitionFeeRate: formData.acquisitionFeeRate != null ? formData.acquisitionFeeRate / 100 : null,
        dispositionFeeRate: formData.dispositionFeeRate != null ? formData.dispositionFeeRate / 100 : null,
        assetManagementFeeRate: formData.assetManagementFeeRate != null ? formData.assetManagementFeeRate / 100 : null,
        feeOffsetEnabled: formData.feeOffsetEnabled,
        feeOffsetPercent: formData.feeOffsetPercent != null ? formData.feeOffsetPercent / 100 : null,
        orgExpensesCap: formData.orgExpensesCap || undefined,
        orgExpensesAmortized: formData.orgExpensesAmortized,
        totalCommittedCapital: formData.totalCommittedCapital || undefined,
        totalCalledCapital: formData.totalCalledCapital || undefined,
        recyclingEnabled: formData.recyclingEnabled,
        recyclingPeriodEnd: formData.recyclingPeriodEnd || undefined,
        recyclingLimit: formData.recyclingLimit != null ? formData.recyclingLimit / 100 : null,
        clawbackEnabled: formData.clawbackEnabled,
        lookbackEnabled: formData.lookbackEnabled,
        escrowEnabled: formData.escrowEnabled,
        escrowPercent: formData.escrowPercent != null ? formData.escrowPercent / 100 : null,
        waterfallType: formData.waterfallType,
        keyPersonsEnabled: formData.keyPersonsEnabled,
        keyPersonNames: formData.keyPersonNames.length > 0 ? formData.keyPersonNames : undefined,
        keyPersonTriggered: formData.keyPersonTriggered,
        keyPersonTriggerDate: formData.keyPersonTriggerDate || undefined,
      };

      await adminService.updateDeal(deal.id, payload as any);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addKeyPerson = () => {
    if (keyPersonInput.trim() && !formData.keyPersonNames.includes(keyPersonInput.trim())) {
      setFormData({
        ...formData,
        keyPersonNames: [...formData.keyPersonNames, keyPersonInput.trim()]
      });
      setKeyPersonInput('');
    }
  };

  const removeKeyPerson = (name: string) => {
    setFormData({
      ...formData,
      keyPersonNames: formData.keyPersonNames.filter(n => n !== name)
    });
  };

  if (!isOpen) return null;

  const sections = [
    { id: 'terms', label: 'Deal Terms', color: 'blue' },
    { id: 'fees', label: 'Fees', color: 'green' },
    { id: 'capital', label: 'Capital', color: 'purple' },
    { id: 'clawback', label: 'Clawback', color: 'amber' },
    { id: 'keyperson', label: 'Key Person', color: 'red' },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Edit Deal Terms</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Section Tabs */}
        <div className="flex gap-2 px-4 pt-4 overflow-x-auto">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeSection === section.id
                  ? `bg-${section.color}-100 text-${section.color}-800 border border-${section.color}-200`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          {/* Deal Terms Section */}
          {activeSection === 'terms' && (
            <div className="space-y-4">
              {/* Waterfall Type */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-3">Distribution Waterfall</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Waterfall Type</label>
                  <select
                    value={formData.waterfallType}
                    onChange={(e) => setFormData({ ...formData, waterfallType: e.target.value as 'AMERICAN' | 'EUROPEAN' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="AMERICAN">American (Independent Class Waterfalls)</option>
                    <option value="EUROPEAN">European (Aggregate Gate)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.waterfallType === 'AMERICAN'
                      ? 'Each share class runs its own independent waterfall. GP promote is earned per-class regardless of other classes.'
                      : 'GP promote is only paid after ALL share classes receive return of capital + full preferred return.'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Investment Period Start</label>
                  <input
                    type="date"
                    value={formData.investmentPeriodStart}
                    onChange={(e) => setFormData({ ...formData, investmentPeriodStart: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Investment Period End</label>
                  <input
                    type="date"
                    value={formData.investmentPeriodEnd}
                    onChange={(e) => setFormData({ ...formData, investmentPeriodEnd: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Fees Section */}
          {activeSection === 'fees' && (
            <div className="space-y-6">
              {/* Asset Management Fee */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <h4 className="text-sm font-semibold text-green-800 mb-3">Asset Management Fee</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%)</label>
                    <PercentInput
                      value={formData.managementFeeRate}
                      onChange={(v) => setFormData({ ...formData, managementFeeRate: v })}
                      min={0}
                      max={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 2.00%"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Basis</label>
                    <select
                      value={formData.managementFeeBasis}
                      onChange={(e) => setFormData({ ...formData, managementFeeBasis: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="COMMITTED_CAPITAL">Committed Capital</option>
                      <option value="INVESTED_CAPITAL">Invested Capital</option>
                      <option value="NET_INVESTED_CAPITAL">Net Invested Capital</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={formData.managementFeeStartDate}
                      onChange={(e) => setFormData({ ...formData, managementFeeStartDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Other Fees */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Acquisition Fee (%)</label>
                  <PercentInput
                    value={formData.acquisitionFeeRate}
                    onChange={(v) => setFormData({ ...formData, acquisitionFeeRate: v })}
                    min={0}
                    max={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 1.00%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Disposition Fee (%)</label>
                  <PercentInput
                    value={formData.dispositionFeeRate}
                    onChange={(v) => setFormData({ ...formData, dispositionFeeRate: v })}
                    min={0}
                    max={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 1.00%"
                  />
                </div>
              </div>

              {/* Fee Offset */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="feeOffsetEnabled"
                    checked={formData.feeOffsetEnabled}
                    onChange={(e) => setFormData({ ...formData, feeOffsetEnabled: e.target.checked })}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="feeOffsetEnabled" className="text-sm font-medium text-gray-700">Enable Fee Offset</label>
                </div>
                {formData.feeOffsetEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Offset Percentage (%)</label>
                    <PercentInput
                      value={formData.feeOffsetPercent}
                      onChange={(v) => setFormData({ ...formData, feeOffsetPercent: v })}
                      min={0}
                      max={100}
                      className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 100.00%"
                    />
                  </div>
                )}
              </div>

              {/* Organizational Expenses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organizational Expenses Cap ($)</label>
                  <CurrencyInput
                    value={formData.orgExpensesCap}
                    onChange={(v) => setFormData({ ...formData, orgExpensesCap: v })}
                    min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., $500,000.00"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="orgExpensesAmortized"
                    checked={formData.orgExpensesAmortized}
                    onChange={(e) => setFormData({ ...formData, orgExpensesAmortized: e.target.checked })}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="orgExpensesAmortized" className="text-sm font-medium text-gray-700">Amortize Organizational Expenses</label>
                </div>
              </div>
            </div>
          )}

          {/* Capital Section */}
          {activeSection === 'capital' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Committed Capital ($)</label>
                  <CurrencyInput
                    value={formData.totalCommittedCapital}
                    onChange={(v) => setFormData({ ...formData, totalCommittedCapital: v })}
                    min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., $50,000,000.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Called Capital ($)</label>
                  <CurrencyInput
                    value={formData.totalCalledCapital}
                    onChange={(v) => setFormData({ ...formData, totalCalledCapital: v })}
                    min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., $25,000,000.00"
                  />
                </div>
              </div>

              {/* Capital Recycling */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="recyclingEnabled"
                    checked={formData.recyclingEnabled}
                    onChange={(e) => setFormData({ ...formData, recyclingEnabled: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="recyclingEnabled" className="text-sm font-medium text-gray-700">Enable Capital Recycling</label>
                </div>
                {formData.recyclingEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recycling Limit (% of Commitments)</label>
                      <input
                        type="number"
                        value={formData.recyclingLimit || ''}
                        onChange={(e) => setFormData({ ...formData, recyclingLimit: e.target.value ? parseFloat(e.target.value) : null })}
                        step="1"
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="e.g., 25"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recycling Period End</label>
                      <input
                        type="date"
                        value={formData.recyclingPeriodEnd}
                        onChange={(e) => setFormData({ ...formData, recyclingPeriodEnd: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clawback Section */}
          {activeSection === 'clawback' && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    id="clawbackEnabled"
                    checked={formData.clawbackEnabled}
                    onChange={(e) => setFormData({ ...formData, clawbackEnabled: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <label htmlFor="clawbackEnabled" className="text-sm font-medium text-gray-700">Enable Clawback Provision</label>
                </div>
                <p className="text-xs text-gray-500 ml-7">
                  GP must return excess carried interest if fund underperforms at final liquidation
                </p>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    id="lookbackEnabled"
                    checked={formData.lookbackEnabled}
                    onChange={(e) => setFormData({ ...formData, lookbackEnabled: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <label htmlFor="lookbackEnabled" className="text-sm font-medium text-gray-700">Enable Lookback Provision</label>
                </div>
                <p className="text-xs text-gray-500 ml-7">
                  Distributions evaluated across fund lifecycle at liquidation
                </p>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    id="escrowEnabled"
                    checked={formData.escrowEnabled}
                    onChange={(e) => setFormData({ ...formData, escrowEnabled: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <label htmlFor="escrowEnabled" className="text-sm font-medium text-gray-700">Enable Escrow Holdback</label>
                </div>
                <p className="text-xs text-gray-500 ml-7">
                  Withhold a percentage of GP promote in escrow for clawback protection
                </p>

                {formData.escrowEnabled && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Escrow Percentage (%)</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="100"
                      value={formData.escrowPercent != null ? formData.escrowPercent : ''}
                      onChange={(e) => setFormData({ ...formData, escrowPercent: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="e.g., 30"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Percentage of GP promote held in escrow per distribution (e.g., 30 = 30%)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Key Person Section */}
          {activeSection === 'keyperson' && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="keyPersonsEnabled"
                    checked={formData.keyPersonsEnabled}
                    onChange={(e) => setFormData({ ...formData, keyPersonsEnabled: e.target.checked })}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="keyPersonsEnabled" className="text-sm font-medium text-gray-700">Enable Key Person Provision</label>
                </div>

                {formData.keyPersonsEnabled && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Key Persons</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={keyPersonInput}
                          onChange={(e) => setKeyPersonInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyPerson())}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                          placeholder="Enter name and press Enter"
                        />
                        <button
                          type="button"
                          onClick={addKeyPerson}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Add
                        </button>
                      </div>
                      {formData.keyPersonNames.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.keyPersonNames.map((name, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                            >
                              {name}
                              <button
                                type="button"
                                onClick={() => removeKeyPerson(name)}
                                className="hover:text-red-900"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="checkbox"
                          id="keyPersonTriggered"
                          checked={formData.keyPersonTriggered}
                          onChange={(e) => setFormData({ ...formData, keyPersonTriggered: e.target.checked })}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="keyPersonTriggered" className="text-sm font-medium text-gray-700">Key Person Event Triggered</label>
                      </div>
                      {formData.keyPersonTriggered && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Date</label>
                          <input
                            type="date"
                            value={formData.keyPersonTriggerDate}
                            onChange={(e) => setFormData({ ...formData, keyPersonTriggerDate: e.target.value })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        <div className="flex gap-3 p-4 border-t bg-gray-50">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" className="flex-1" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Deal Terms'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Add Distribution Modal
// =============================================================================

interface AddDistributionModalProps {
  dealId: string;
  shareClasses: ShareClass[];
  onClose: () => void;
  onCreated: () => void;
}

function AddDistributionModal({ dealId, shareClasses, onClose, onCreated }: AddDistributionModalProps) {
  const [formData, setFormData] = useState({
    type: 'QUARTERLY',
    totalAmount: 0,
    distributionDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'ACH',
    notes: '',
  });
  const [selectedShareClassIds, setSelectedShareClassIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleShareClass = (id: string) => {
    setSelectedShareClassIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.createDistribution({
        dealId,
        type: formData.type,
        totalAmount: formData.totalAmount,
        distributionDate: formData.distributionDate,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes || undefined,
        shareClassIds: selectedShareClassIds.length > 0 ? selectedShareClassIds : undefined,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Record Distribution</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="QUARTERLY">Quarterly</option>
              <option value="ANNUAL">Annual</option>
              <option value="SPECIAL">Special</option>
              <option value="RETURN_OF_CAPITAL">Return of Capital</option>
              <option value="REFINANCE">Refinance</option>
              <option value="SALE">Sale</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.totalAmount ? formData.totalAmount.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, totalAmount: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="10,000"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Allocations will be calculated automatically based on ownership percentages.
            </p>
          </div>

          {shareClasses.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Share Classes</label>
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {shareClasses.map((sc) => (
                  <label key={sc.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedShareClassIds.includes(sc.id)}
                      onChange={() => toggleShareClass(sc.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-900">{sc.name}</span>
                    <span className="text-xs text-gray-500">({sc.code})</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {selectedShareClassIds.length === 0
                  ? 'All investors will receive allocations (no filter)'
                  : `Only investors in selected class${selectedShareClassIds.length > 1 ? 'es' : ''} will receive allocations (pro rata)`}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Date *</label>
            <input
              type="date"
              value={formData.distributionDate}
              onChange={(e) => setFormData({ ...formData, distributionDate: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ACH">ACH Transfer</option>
              <option value="CHECK">Check</option>
              <option value="WIRE">Wire Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Optional notes about this distribution"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Recording...' : 'Record Distribution'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Distribution Modal
// =============================================================================

interface EditDistributionModalProps {
  distribution: DealDetails['distributions'][0];
  existingShareClasses: ShareClass[];
  onClose: () => void;
  onSaved: () => void;
}

function EditDistributionModal({ distribution, existingShareClasses, onClose, onSaved }: EditDistributionModalProps) {
  const [formData, setFormData] = useState({
    type: distribution.type,
    totalAmount: distribution.totalAmount,
    distributionDate: distribution.distributionDate.split('T')[0],
    paymentMethod: distribution.paymentMethod,
    notes: distribution.notes || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Share class editing state - use share classes already loaded by parent
  const [editSelectedShareClassIds, setEditSelectedShareClassIds] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.updateDistribution(distribution.id, {
        type: formData.type,
        totalAmount: formData.totalAmount,
        distributionDate: formData.distributionDate,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes || undefined,
        shareClassIds: existingShareClasses.length > 0 ? editSelectedShareClassIds : undefined,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [showDistDeleteConfirm, setShowDistDeleteConfirm] = useState(false);
  const [distDeleteText, setDistDeleteText] = useState('');

  const handleDelete = async () => {
    if (distDeleteText.toLowerCase() !== 'proceed') return;

    setIsSubmitting(true);
    try {
      await adminService.deleteDistribution(distribution.id);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Distribution</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="QUARTERLY">Quarterly</option>
              <option value="ANNUAL">Annual</option>
              <option value="SPECIAL">Special</option>
              <option value="RETURN_OF_CAPITAL">Return of Capital</option>
              <option value="REFINANCE">Refinance</option>
              <option value="SALE">Sale</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.totalAmount ? formData.totalAmount.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, totalAmount: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Changing the amount will recalculate all investor allocations.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Date *</label>
            <input
              type="date"
              value={formData.distributionDate}
              onChange={(e) => setFormData({ ...formData, distributionDate: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ACH">ACH Transfer</option>
              <option value="CHECK">Check</option>
              <option value="WIRE">Wire Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Share Class Selection */}
          {existingShareClasses.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Share Classes</label>
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {existingShareClasses.map((sc) => (
                  <label key={sc.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editSelectedShareClassIds.includes(sc.id)}
                      onChange={() => {
                        setEditSelectedShareClassIds(prev =>
                          prev.includes(sc.id) ? prev.filter(x => x !== sc.id) : [...prev, sc.id]
                        );
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-900">{sc.name}</span>
                    <span className="text-xs text-gray-500">({sc.code})</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {editSelectedShareClassIds.length === 0
                  ? 'All investors will receive allocations (no filter)'
                  : `Only investors in selected class${editSelectedShareClassIds.length > 1 ? 'es' : ''} will receive allocations`}
              </p>
              <p className="mt-0.5 text-xs text-amber-600">
                Changing share classes will recalculate all investor allocations.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setDistDeleteText(''); setShowDistDeleteConfirm(true); }}
              disabled={isSubmitting}
              className="text-red-600 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>

        {/* Distribution Delete Confirmation Modal */}
        {showDistDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Distribution</h3>
              </div>
              <p className="text-sm text-red-600 mb-4">
                This will permanently delete this distribution and all allocation records. This cannot be undone.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-bold">proceed</span> to confirm:
                </label>
                <input
                  type="text"
                  value={distDeleteText}
                  onChange={(e) => setDistDeleteText(e.target.value)}
                  placeholder="Type 'proceed' to confirm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDistDeleteConfirm(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={distDeleteText.toLowerCase() !== 'proceed' || isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Distribution'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Add Investment Modal
// =============================================================================

interface AddInvestmentModalProps {
  dealId: string;
  dealName: string;
  totalEquityRaised: number;
  shareClasses: ShareClass[];
  onClose: () => void;
  onCreated: () => void;
}

function AddInvestmentModal({ dealId, dealName, totalEquityRaised, shareClasses: initialShareClasses, onClose, onCreated }: AddInvestmentModalProps) {
  const [investors, setInvestors] = useState<InvestorWithProfiles[]>([]);
  const [loadingInvestors, setLoadingInvestors] = useState(true);
  const [selectedInvestorId, setSelectedInvestorId] = useState<string>('');
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [selectedShareClassId, setSelectedShareClassId] = useState<string>('');
  const [investmentAmount, setInvestmentAmount] = useState<number>(0);
  const [investmentDate, setInvestmentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [investorRole, setInvestorRole] = useState<'LP' | 'GP'>('LP');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareClasses, setShareClasses] = useState<ShareClass[]>(initialShareClasses);
  const [loadingShareClasses, setLoadingShareClasses] = useState(false);

  // Load all investors on mount
  useEffect(() => {
    const loadInvestors = async () => {
      try {
        setLoadingInvestors(true);
        const result = await adminService.listInvestors({ limit: 500 });
        setInvestors(result.investors);
      } catch (err) {
        setError('Failed to load investors');
        console.error(err);
      } finally {
        setLoadingInvestors(false);
      }
    };
    loadInvestors();
  }, []);

  // Load share classes if not already provided
  useEffect(() => {
    const loadShareClasses = async () => {
      if (initialShareClasses.length === 0) {
        try {
          setLoadingShareClasses(true);
          const classes = await adminService.getShareClasses(dealId);
          setShareClasses(classes);
        } catch (err) {
          console.error('Failed to load share classes:', err);
        } finally {
          setLoadingShareClasses(false);
        }
      }
    };
    loadShareClasses();
  }, [dealId, initialShareClasses.length]);

  // Set default share class when share classes load
  useEffect(() => {
    if (shareClasses.length > 0 && !selectedShareClassId) {
      const defaultClass = shareClasses.find(sc => sc.isDefault) || shareClasses[0];
      if (defaultClass) {
        setSelectedShareClassId(defaultClass.id);
      }
    }
  }, [shareClasses, selectedShareClassId]);

  // Get selected investor's profiles
  const selectedInvestor = investors.find(inv => inv.id === selectedInvestorId);
  const profiles = selectedInvestor?.profiles || [];

  // Reset profile selection when investor changes
  useEffect(() => {
    setSelectedProfileId('');
  }, [selectedInvestorId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedInvestorId) {
      setError('Please select an investor');
      return;
    }
    if (!selectedProfileId) {
      setError('Please select an investment profile');
      return;
    }
    if (investmentAmount <= 0) {
      setError('Please enter a valid investment amount');
      return;
    }
    if (shareClasses.length > 0 && !selectedShareClassId) {
      setError('Please select a share class');
      return;
    }

    setIsSubmitting(true);

    try {
      await adminService.createInvestment({
        profileId: selectedProfileId,
        dealId,
        investmentAmount,
        investmentDate,
        shareClassId: selectedShareClassId || undefined,
        investorRole,
      });
      await onCreated();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatProfileName = (profile: { type: string; legalName?: string; entityName?: string }) => {
    const name = profile.entityName || profile.legalName || 'Unnamed';
    return `${name} (${profile.type})`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Investment</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Adding investment to <span className="font-medium">{dealName}</span>
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Investor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investor *</label>
            {loadingInvestors ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Loading investors...
              </div>
            ) : (
              <select
                value={selectedInvestorId}
                onChange={(e) => setSelectedInvestorId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select an investor</option>
                {investors.map((investor) => (
                  <option key={investor.id} value={investor.id}>
                    {investor.name || `${investor.firstName} ${investor.lastName}`} ({investor.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Profile Selection - Only shown after investor is selected */}
          {selectedInvestorId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Investment Profile *</label>
              {profiles.length === 0 ? (
                <div className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-amber-50 text-amber-700 text-sm">
                  This investor has no profiles set up. Please add a profile first.
                </div>
              ) : (
                <select
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a profile</option>
                  {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {formatProfileName(profile)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Investment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={investmentAmount ? investmentAmount.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setInvestmentAmount(parseFloat(value) || 0);
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="100,000"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Ownership % will be calculated automatically based on total equity raised (${totalEquityRaised.toLocaleString()}).
            </p>
          </div>

          {/* Share Class Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Share Class{shareClasses.length > 0 ? ' *' : ''}
            </label>
            {loadingShareClasses ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Loading share classes...
              </div>
            ) : shareClasses.length > 0 ? (
              <select
                value={selectedShareClassId}
                onChange={(e) => setSelectedShareClassId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a share class</option>
                {shareClasses.map((sc) => (
                  <option key={sc.id} value={sc.id}>
                    {sc.name} ({sc.code})
                    {sc.isDefault ? ' - Default' : ''}
                    {sc.preferredReturnRate ? ` - ${(sc.preferredReturnRate * 100).toFixed(1)}% Pref` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm">
                No share classes configured for this deal
              </div>
            )}
            {shareClasses.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Required: Assign investment to a share class for waterfall distributions
              </p>
            )}
          </div>

          {/* Investor Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investor Role *</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setInvestorRole('LP')}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  investorRole === 'LP'
                    ? 'bg-primary-50 border-primary-300 text-primary-700 ring-2 ring-primary-200'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                LP (Limited Partner)
              </button>
              <button
                type="button"
                onClick={() => setInvestorRole('GP')}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  investorRole === 'GP'
                    ? 'bg-blue-50 border-blue-300 text-blue-700 ring-2 ring-blue-200'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                GP (General Partner)
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              LP investors receive pro-rata distributions. GP co-investors receive the same pro-rata treatment plus may receive promoted interest.
            </p>
          </div>

          {/* Investment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Date</label>
            <input
              type="date"
              value={investmentDate}
              onChange={(e) => setInvestmentDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || !selectedInvestorId || !selectedProfileId || profiles.length === 0}
            >
              {isSubmitting ? 'Adding...' : 'Add Investment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Investment Modal
// =============================================================================

interface EditInvestmentModalProps {
  investment: DealDetails['investments'][0];
  shareClasses: ShareClass[];
  onClose: () => void;
  onSaved: () => void;
}

function EditInvestmentModal({ investment, shareClasses, onClose, onSaved }: EditInvestmentModalProps) {
  const [formData, setFormData] = useState({
    isActive: investment.isActive,
    shareClassId: investment.shareClassId || '',
    investorRole: investment.investorRole || 'LP',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (shareClasses.length > 0 && !formData.shareClassId) {
      setError('Please select a share class');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await adminService.updateInvestment(investment.id, {
        isActive: formData.isActive,
        shareClassId: formData.shareClassId || null,
        investorRole: formData.investorRole,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <h2 className="text-lg font-semibold text-gray-900 mb-1">Edit Investment</h2>
          <p className="text-sm text-gray-500 mb-4">{investment.investorName} - {investment.profileName}</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Investment Status</p>
                <p className="text-sm text-gray-500">
                  {formData.isActive ? 'Active - Included in distributions' : 'Deactivated - Excluded from future distributions'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  formData.isActive ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {!formData.isActive && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Deactivated investments will not receive future distributions but historical records will be preserved.
                </p>
              </div>
            )}

            {/* Share Class */}
            {shareClasses.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Share Class *</label>
                <select
                  value={formData.shareClassId}
                  onChange={(e) => setFormData({ ...formData, shareClassId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a share class</option>
                  {shareClasses.map((sc) => (
                    <option key={sc.id} value={sc.id}>
                      {sc.name} ({sc.code})
                      {sc.isDefault ? ' - Default' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Investor Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Investor Role</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, investorRole: 'LP' })}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    formData.investorRole === 'LP'
                      ? 'bg-primary-50 border-primary-300 text-primary-700 ring-2 ring-primary-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  LP
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, investorRole: 'GP' })}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    formData.investorRole === 'GP'
                      ? 'bg-blue-50 border-blue-300 text-blue-700 ring-2 ring-blue-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  GP
                </button>
              </div>
            </div>

            {investment.totalDistributed > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  This investor has received ${investment.totalDistributed.toLocaleString()} in distributions.
                </p>
              </div>
            )}

            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              To make further edits, please visit the Investor's profile and their investment{' '}
              <Link to={`/admin/investors/${investment.investorId}`} className="text-primary-600 hover:text-primary-800 font-medium underline">
                HERE
              </Link>.
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Share Class Modal
// =============================================================================

interface ShareClassModalProps {
  dealId: string;
  shareClass: ShareClass | null;
  onClose: () => void;
  onSaved: () => void;
}

// Waterfall tier for the modal form
interface TierFormData {
  id?: string;
  tierNumber: number;
  name: string;
  hurdleType: 'IRR' | 'EQUITY_MULTIPLE' | 'CASH_ON_CASH';
  hurdleValue: number | null;
  gpPromotePercent: number;
  // Calculated fields (derived from base split and promote)
  effectiveLpPercent: number;
  effectiveGpPercent: number;
}

function ShareClassModal({ dealId, shareClass, onClose, onSaved }: ShareClassModalProps) {
  const [formData, setFormData] = useState<CreateShareClassInput & { id?: string }>({
    dealId,
    name: shareClass?.name || 'Class A',
    code: shareClass?.code || 'A',
    description: shareClass?.description || '',
    priority: shareClass?.priority || 1,
    isDefault: shareClass?.isDefault || false,
    lpSplitPercent: shareClass?.lpSplitPercent ?? 0,
    gpSplitPercent: shareClass?.gpSplitPercent ?? 1,
    preferredReturnRate: shareClass?.preferredReturnRate ?? 0,
    preferredReturnType: shareClass?.preferredReturnType || 'TRUE_PREFERRED',
    compoundingMethod: shareClass?.compoundingMethod || 'ANNUAL',
    isCumulative: shareClass?.isCumulative ?? true,
    catchUpPercent: shareClass?.catchUpPercent ?? 0,
    carriedInterestRate: shareClass?.carriedInterestRate,
    maxEquityMultiple: shareClass?.maxEquityMultiple ?? null,
    minimumInvestment: shareClass?.minimumInvestment,
  });

  // Dynamic waterfall tiers
  const [numberOfTiers, setNumberOfTiers] = useState<number>(
    shareClass?.waterfallTiers?.length || 0
  );
  const [tiers, setTiers] = useState<TierFormData[]>(() => {
    if (shareClass?.waterfallTiers && shareClass.waterfallTiers.length > 0) {
      return shareClass.waterfallTiers.map((tier) => ({
        id: tier.id,
        tierNumber: tier.tierNumber,
        name: tier.name,
        hurdleType: tier.hurdleType,
        hurdleValue: tier.hurdleValue ? Math.round(tier.hurdleValue * 10000) / 100 : null, // Convert to percentage, rounded
        gpPromotePercent: Math.round(tier.gpPromotePercent * 10000) / 100,
        effectiveLpPercent: Math.round(tier.lpSplitPercent * 10000) / 100,
        effectiveGpPercent: Math.round(tier.gpSplitPercent * 10000) / 100,
      }));
    }
    return [];
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingTiers, setSavingTiers] = useState(false);

  // Calculate effective LP/GP split based on base split and promote
  const calculateEffectiveSplit = (promotePercent: number): { lp: number; gp: number } => {
    const baseLp = (formData.lpSplitPercent ?? 0) * 100;
    const baseGp = (formData.gpSplitPercent ?? 1) * 100;

    // Effective LP = Base LP × (1 - Promote%)
    // Effective GP = Base GP + (Base LP × Promote%)
    const effectiveLp = baseLp * (1 - promotePercent / 100);
    const effectiveGp = baseGp + (baseLp * promotePercent / 100);

    return { lp: effectiveLp, gp: effectiveGp };
  };

  // Update tier with recalculated effective splits
  const updateTier = (index: number, updates: Partial<TierFormData>) => {
    setTiers((prev) => {
      const newTiers = [...prev];
      const tier = { ...newTiers[index], ...updates };

      // Recalculate effective splits if promote changed
      if ('gpPromotePercent' in updates) {
        const { lp, gp } = calculateEffectiveSplit(tier.gpPromotePercent);
        tier.effectiveLpPercent = lp;
        tier.effectiveGpPercent = gp;
      }

      newTiers[index] = tier;
      return newTiers;
    });
  };

  // Handle number of tiers change
  const handleNumberOfTiersChange = (newCount: number) => {
    const count = Math.max(0, Math.min(10, newCount)); // Limit to 0-10 tiers
    setNumberOfTiers(count);

    if (count > tiers.length) {
      // Add new tiers
      const newTiers = [...tiers];
      for (let i = tiers.length; i < count; i++) {
        const { lp, gp } = calculateEffectiveSplit(0);
        newTiers.push({
          tierNumber: i + 1,
          name: `Tier ${i + 1}`,
          hurdleType: 'IRR',
          hurdleValue: null,
          gpPromotePercent: 0,
          effectiveLpPercent: lp,
          effectiveGpPercent: gp,
        });
      }
      setTiers(newTiers);
    } else if (count < tiers.length) {
      // Remove tiers
      setTiers(tiers.slice(0, count));
    }
  };

  // Recalculate all effective splits when base split changes
  const handleLpSplitChange = (value: number) => {
    setFormData({ ...formData, lpSplitPercent: value, gpSplitPercent: 1 - value });

    // Recalculate all tier effective splits
    setTiers((prev) => prev.map((tier) => {
      const baseLp = value * 100;
      const baseGp = (1 - value) * 100;
      const effectiveLp = baseLp * (1 - tier.gpPromotePercent / 100);
      const effectiveGp = baseGp + (baseLp * tier.gpPromotePercent / 100);
      return { ...tier, effectiveLpPercent: effectiveLp, effectiveGpPercent: effectiveGp };
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      let savedShareClass: ShareClass;

      if (shareClass) {
        savedShareClass = await adminService.updateShareClass(shareClass.id, formData as UpdateShareClassInput);
      } else {
        savedShareClass = await adminService.createShareClass(formData);
      }

      // Save waterfall tiers if there are any
      if (tiers.length > 0) {
        setSavingTiers(true);

        // Delete existing tiers that are no longer in the list
        if (shareClass?.waterfallTiers) {
          for (const existingTier of shareClass.waterfallTiers) {
            if (!tiers.find((t) => t.id === existingTier.id)) {
              await adminService.deleteWaterfallTier(existingTier.id);
            }
          }
        }

        // Create or update tiers
        for (const tier of tiers) {
          const tierData = {
            tierNumber: tier.tierNumber,
            name: tier.name,
            hurdleType: tier.hurdleType,
            hurdleValue: tier.hurdleValue !== null ? tier.hurdleValue / 100 : undefined,
            lpSplitPercent: tier.effectiveLpPercent / 100,
            gpSplitPercent: tier.effectiveGpPercent / 100,
            gpPromotePercent: tier.gpPromotePercent / 100,
          };

          if (tier.id) {
            await adminService.updateWaterfallTier(tier.id, tierData);
          } else {
            await adminService.createWaterfallTier(savedShareClass.id, tierData);
          }
        }
      } else if (shareClass?.waterfallTiers) {
        // Delete all existing tiers if none specified
        for (const existingTier of shareClass.waterfallTiers) {
          await adminService.deleteWaterfallTier(existingTier.id);
        }
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
      setSavingTiers(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            {shareClass ? 'Edit Share Class' : 'Create Share Class'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Class A"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  placeholder="e.g., A"
                  maxLength={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <IntegerInput
                  value={formData.priority || 1}
                  onChange={(v) => setFormData({ ...formData, priority: v })}
                  min={1}
                  max={99}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Lower = higher priority</p>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isDefault || false}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Default for new investments</span>
                </label>
              </div>
            </div>
          </div>

          {/* Base LP/GP Split */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Base LP/GP Split</h3>
            <p className="text-xs text-gray-500">This is the starting split before any promotes are applied</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LP Split (%)</label>
                <PercentInput
                  value={Math.round((formData.lpSplitPercent ?? 0) * 10000) / 100}
                  onChange={(v) => handleLpSplitChange((v ?? 0) / 100)}
                  min={0}
                  max={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GP Split (%)</label>
                <PercentInput
                  value={Math.round((formData.gpSplitPercent ?? 1) * 10000) / 100}
                  onChange={() => {}}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
              </div>
            </div>
          </div>

          {/* Preferred Return */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Preferred Return</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Return (%)</label>
                <PercentInput
                  value={(formData.preferredReturnRate ?? 0) * 100}
                  onChange={(v) => setFormData({ ...formData, preferredReturnRate: (v ?? 0) / 100 })}
                  min={0}
                  max={50}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.preferredReturnType || 'TRUE_PREFERRED'}
                  onChange={(e) => setFormData({ ...formData, preferredReturnType: e.target.value as 'TRUE_PREFERRED' | 'PARI_PASSU' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="TRUE_PREFERRED">True Preferred</option>
                  <option value="PARI_PASSU">Pari Passu</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compounding</label>
                <select
                  value={formData.compoundingMethod || 'ANNUAL'}
                  onChange={(e) => setFormData({ ...formData, compoundingMethod: e.target.value as 'NONE' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="NONE">None</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="ANNUAL">Annual</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isCumulative ?? true}
                  onChange={(e) => setFormData({ ...formData, isCumulative: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Cumulative (unpaid preferred return accrues)</span>
              </label>
            </div>
          </div>

          {/* Waterfall Tiers - Dynamic Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Waterfall Tiers</h3>
                <p className="text-xs text-gray-500 mt-1">Define promote tiers based on IRR or equity multiple hurdles</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Number of Tiers:</label>
                <IntegerInput
                  value={numberOfTiers}
                  onChange={(v) => handleNumberOfTiersChange(v)}
                  min={0}
                  max={10}
                  className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {numberOfTiers > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-10">#</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Hurdle Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Hurdle Value</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">GP Promote %</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 bg-green-50">Eff. LP %</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 bg-blue-50">Eff. GP %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {tiers.map((tier, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-500 font-medium">{tier.tierNumber}</td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={tier.name}
                            onChange={(e) => updateTier(index, { name: e.target.value })}
                            placeholder={`Tier ${index + 1}`}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={tier.hurdleType}
                            onChange={(e) => updateTier(index, { hurdleType: e.target.value as 'IRR' | 'EQUITY_MULTIPLE' | 'CASH_ON_CASH' })}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="IRR">IRR</option>
                            <option value="EQUITY_MULTIPLE">Equity Multiple</option>
                            <option value="CASH_ON_CASH">Cash-on-Cash</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {tier.hurdleType === 'EQUITY_MULTIPLE' ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={tier.hurdleValue ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (/^\d*\.?\d*$/.test(raw) || raw === '') {
                                    updateTier(index, { hurdleValue: raw ? parseFloat(raw) : null });
                                  }
                                }}
                                placeholder="1.5"
                                className="w-16 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                              />
                              <span className="text-xs text-gray-500">x</span>
                            </div>
                          ) : (
                            <PercentInput
                              value={tier.hurdleValue}
                              onChange={(v) => updateTier(index, { hurdleValue: v })}
                              min={0}
                              placeholder={tier.hurdleType === 'CASH_ON_CASH' ? '8.00%' : '8.00%'}
                              className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <PercentInput
                            value={tier.gpPromotePercent}
                            onChange={(v) => updateTier(index, { gpPromotePercent: v ?? 0 })}
                            min={0}
                            max={100}
                            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-right bg-green-50">
                          <span className="text-sm font-semibold text-green-700">
                            {tier.effectiveLpPercent.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right bg-blue-50">
                          <span className="text-sm font-semibold text-blue-700">
                            {tier.effectiveGpPercent.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {numberOfTiers === 0 && (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">No waterfall tiers configured. Enter a number above to add tiers.</p>
              </div>
            )}
          </div>

          {/* Additional Options */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Additional Options</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catch-Up (%)</label>
                <PercentInput
                  value={(formData.catchUpPercent ?? 0) * 100}
                  onChange={(v) => setFormData({ ...formData, catchUpPercent: (v ?? 0) / 100 })}
                  min={0}
                  max={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Investment ($)</label>
                <CurrencyInput
                  value={formData.minimumInvestment ?? null}
                  onChange={(v) => setFormData({ ...formData, minimumInvestment: v ?? undefined })}
                  min={0}
                  placeholder="e.g., $50,000.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Equity Multiple (x)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.maxEquityMultiple != null ? formData.maxEquityMultiple : ''}
                  onChange={(e) => setFormData({ ...formData, maxEquityMultiple: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="e.g., 1.50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no cap. E.g., 1.50 = 1.5x equity multiple cap.</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting || savingTiers}>
              {isSubmitting || savingTiers ? 'Saving...' : (shareClass ? 'Save Changes' : 'Create Share Class')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Upload Tax Financials Modal
// =============================================================================

interface UploadDocumentModalProps {
  isOpen: boolean;
  dealId: string;
  dealName: string;
  onClose: () => void;
  onUploaded: () => void;
}

function UploadDocumentModal({ isOpen, dealId, dealName, onClose, onUploaded }: UploadDocumentModalProps) {
  const currentYear = new Date().getFullYear();
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('OTHER');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentTypes = [
    { value: 'FINANCIAL_STATEMENT', label: 'Tax / Financial Statement' },
    { value: 'K1', label: 'K-1' },
    { value: 'QUARTERLY_REPORT', label: 'Quarterly Report' },
    { value: 'PPM', label: 'PPM' },
    { value: 'SUBSCRIPTION_AGREEMENT', label: 'Subscription Agreement' },
    { value: 'OPERATING_AGREEMENT', label: 'Operating Agreement' },
    { value: 'OTHER', label: 'Other' },
  ];

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setDocType('OTHER');
      setName('');
      setDescription('');
      setTaxYear(currentYear - 1);
      setError(null);
    }
  }, [isOpen, currentYear]);

  // Auto-generate name based on type
  const getDefaultName = () => {
    switch (docType) {
      case 'FINANCIAL_STATEMENT': return `${dealName} - Financial Statement ${taxYear}`;
      case 'K1': return `${dealName} - K-1 ${taxYear}`;
      case 'QUARTERLY_REPORT': return `${dealName} - Quarterly Report`;
      default: return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const docName = name || getDefaultName() || file.name.replace(/\.[^/.]+$/, '');
      const docDescription = description || (
        docType === 'FINANCIAL_STATEMENT' ? `Financial statement for tax year ${taxYear}` :
        docType === 'K1' ? `K-1 for tax year ${taxYear}` :
        undefined
      );
      await adminService.uploadDocument(file, {
        dealId,
        type: docType as any,
        name: docName,
        description: docDescription,
      });
      onUploaded();
      onClose();
    } catch (err) {
      console.error('Failed to upload document:', err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upload Document</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Upload to: <span className="font-medium text-gray-900">{dealName}</span>
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Tax Year — shown for Financial Statement and K-1 */}
          {(docType === 'FINANCIAL_STATEMENT' || docType === 'K1') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Year *</label>
              <select
                value={taxYear}
                onChange={(e) => setTaxYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {[...Array(10)].map((_, i) => {
                  const year = currentYear - i;
                  return (
                    <option key={year} value={year}>{year}</option>
                  );
                })}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={getDefaultName() || 'Enter document name'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-400">Leave blank to auto-generate from file name</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document File *</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-500 transition-colors">
              <input
                type="file"
                accept=".pdf,.PDF,.xls,.xlsx,.doc,.docx,.csv,.png,.jpg,.jpeg,.ppt,.pptx,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="deal-doc-file-input"
              />
              <label htmlFor="deal-doc-file-input" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                    <span className="text-sm font-medium text-gray-900">{file.name}</span>
                  </div>
                ) : (
                  <div>
                    <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Images, CSV</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isUploading || !file}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>

          <div className="text-center">
            <Link
              to={`/admin/documents/bulk-upload`}
              className="text-xs text-primary-600 hover:text-primary-800"
              onClick={onClose}
            >
              Need to upload multiple files? Use Bulk Upload
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canEditDeal = hasPermission('deals', 'edit');
  const canDeleteDeal = hasPermission('deals', 'delete');
  const canUploadDocument = hasPermission('documents', 'upload');
  const [deal, setDeal] = useState<DealDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'investors' | 'distributions' | 'documents' | 'share-classes' | 'waterfall' | 'fund-terms'>('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
  const [showAddDistributionModal, setShowAddDistributionModal] = useState(false);
  const [editingDistribution, setEditingDistribution] = useState<DealDetails['distributions'][0] | null>(null);
  const [editingInvestment, setEditingInvestment] = useState<DealDetails['investments'][0] | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{ id: string; name: string; mimeType?: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Share Classes state
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [loadingShareClasses, setLoadingShareClasses] = useState(false);
  const [showShareClassModal, setShowShareClassModal] = useState(false);
  const [editingShareClass, setEditingShareClass] = useState<ShareClass | null>(null);
  const [expandedShareClass, setExpandedShareClass] = useState<string | null>(null);

  const [showFundTermsModal, setShowFundTermsModal] = useState(false);
  const [expandedContribution, setExpandedContribution] = useState<string | null>(null);
  const [promoteSearch, setPromoteSearch] = useState('');
  const [promoteDropdownOpen, setPromoteDropdownOpen] = useState(false);

  // Delete confirmation modal state (shared for share classes, tiers, distributions)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalText, setDeleteModalText] = useState('');
  const [deleteModalTarget, setDeleteModalTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const [deleteModalLoading, setDeleteModalLoading] = useState(false);

  // Tax Financials upload state
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);

  // Custom fields state
  const [editingCustomFields, setEditingCustomFields] = useState(false);
  const [customFields, setCustomFields] = useState<{ key: string; value: string }[]>([]);
  const [savingCustomFields, setSavingCustomFields] = useState(false);
  const [dragFieldIndex, setDragFieldIndex] = useState<number | null>(null);
  const [dragOverFieldIndex, setDragOverFieldIndex] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      loadDeal();
      // Load share classes immediately for tab count display
      loadShareClasses();
    }
  }, [id]);

  // Also load share classes when tab changes (in case they were modified)
  useEffect(() => {
    if (activeTab === 'share-classes' && id) {
      loadShareClasses();
    }
  }, [activeTab, id]);

  const loadShareClasses = async () => {
    if (!id) return;
    try {
      setLoadingShareClasses(true);
      const classes = await adminService.getShareClasses(id);
      setShareClasses(classes);
    } catch (err) {
      console.error('Failed to load share classes:', err);
    } finally {
      setLoadingShareClasses(false);
    }
  };

  const loadDeal = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await adminService.getDeal(id!);
      setDeal(data);
    } catch (err) {
      setError('Failed to load deal details');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!deal) return;

    try {
      setIsDeleting(true);
      await adminService.deleteDeal(deal.id);
      navigate('/admin/deals');
    } catch (err) {
      setError(getApiError(err).message);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const startEditingCustomFields = () => {
    const existing = (deal as any)?.customFields || [];
    setCustomFields(Array.isArray(existing) ? [...existing] : []);
    setEditingCustomFields(true);
  };

  const handleSaveCustomFields = async () => {
    if (!deal) return;
    try {
      setSavingCustomFields(true);
      const filtered = customFields.filter(f => f.key.trim());
      await adminService.updateDeal(deal.id, { customFields: filtered });
      setEditingCustomFields(false);
      loadDeal(true);
    } catch (err) {
      alert(getApiError(err).message);
    } finally {
      setSavingCustomFields(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatPercentage = (value: number) => {
    // ownershipPercentage is already stored as percentage (e.g., 37.04 means 37.04%)
    return `${value.toFixed(2)}%`;
  };

  // Calculate initial equity vs additional capital calls
  const calculateEquityBreakdown = () => {
    if (!deal) return { initialEquity: 0, additionalCapitalCalls: 0 };

    let initialEquity = 0;
    let additionalCapitalCalls = 0;

    for (const investment of deal.investments) {
      if (investment.contributions && investment.contributions.length > 0) {
        // Sort contributions by funding date
        const sortedContributions = [...investment.contributions].sort(
          (a, b) => new Date(a.fundingDate).getTime() - new Date(b.fundingDate).getTime()
        );
        // First contribution is initial, rest are additional
        initialEquity += sortedContributions[0].amount;
        for (let i = 1; i < sortedContributions.length; i++) {
          additionalCapitalCalls += sortedContributions[i].amount;
        }
      } else {
        // No contributions tracked, treat entire amount as initial
        initialEquity += investment.investmentAmount;
      }
    }

    return { initialEquity, additionalCapitalCalls };
  };

  const equityBreakdown = deal ? calculateEquityBreakdown() : { initialEquity: 0, additionalCapitalCalls: 0 };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      MULTIFAMILY: 'Multifamily',
      RETAIL: 'Retail',
      OFFICE: 'Office',
      INDUSTRIAL: 'Industrial',
      MIXED_USE: 'Mixed Use',
      HOTEL: 'Hotel',
      SELF_STORAGE: 'Self Storage',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDistributionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      QUARTERLY: 'Quarterly',
      ANNUAL: 'Annual',
      SPECIAL: 'Special',
      RETURN_OF_CAPITAL: 'Return of Capital',
      REFINANCE: 'Refinance',
      SALE: 'Sale',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      QUARTERLY_REPORT: 'Quarterly Report',
      K1: 'K-1',
      PPM: 'PPM',
      SUBSCRIPTION_AGREEMENT: 'Subscription Agreement',
      FINANCIAL_STATEMENT: 'Financial Statement',
      OPERATING_AGREEMENT: 'Operating Agreement',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      ACH: 'ACH Transfer',
      CHECK: 'Check',
      WIRE: 'Wire Transfer',
    };
    return labels[method] || method;
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('deals', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Deal not found'}</p>
        <Link to="/admin/deals" className="text-primary-600 hover:underline mt-4 inline-block">
          Back to Deals
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/admin/deals" className="hover:text-primary-600">Deals</Link>
            <span>/</span>
            <span>{deal.name}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{deal.name}</h1>
          <p className="text-gray-600 mt-1">
            {deal.address}, {deal.city}, {deal.state} {deal.zipCode}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            deal.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {deal.isActive ? 'Active' : 'Inactive'}
          </span>
          {canEditDeal && (
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Edit Deal
            </button>
          )}
          {canDeleteDeal && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Overview: Stats + Deal Info (left) | Map (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left Column: Stats + Deal Info */}
        <div className="lg:col-span-3 space-y-4">
          {/* Stats Cards - 2x2 Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Equity Raised</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(deal.totalEquityRaised)}</p>
              {equityBreakdown.additionalCapitalCalls > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Initial Equity</span>
                    <span className="text-gray-700">{formatCurrency(equityBreakdown.initialEquity)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Additional Capital Calls</span>
                    <span className="text-gray-700">{formatCurrency(equityBreakdown.additionalCapitalCalls)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Invested</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(deal.totalInvested)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Distributed</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(deal.totalDistributed)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Investors</p>
              <p className="text-2xl font-bold text-gray-900">{deal.investorCount}</p>
            </div>
          </div>

          {/* Deal Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex gap-6">
              {/* Deal Image */}
              <div className="flex-shrink-0">
                <div className="relative w-40 h-40 bg-gray-100 rounded-lg overflow-hidden">
                  <DealImage
                    dealId={deal.id}
                    dealName={deal.name}
                    imageUrl={deal.imageUrl}
                    className="w-full h-full object-cover"
                    fallbackClassName="flex items-center justify-center h-full bg-gray-100"
                    iconClassName="w-12 h-12 text-gray-400"
                  />
                  {canEditDeal && <label className={`absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity cursor-pointer ${uploadingImage ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}>
                    {uploadingImage ? (
                      <span className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <span className="text-white text-sm font-medium">Upload Photo</span>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      className="hidden"
                      disabled={uploadingImage}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        // Validate file type and size
                        const validation = validateImageFile(file);
                        if (!validation.valid) {
                          alert(validation.error);
                          e.target.value = ''; // Reset input
                          return;
                        }

                        setUploadingImage(true);
                        try {
                          // Compress image to prevent browser crashes
                          const compressedFile = await compressImage(file, {
                            maxWidth: 1920,
                            maxHeight: 1080,
                            quality: 0.85,
                            maxSizeMB: 2,
                          });

                          await adminService.uploadDealImage(deal.id, compressedFile);
                          loadDeal(true);
                        } catch (err) {
                          console.error('Failed to upload image:', err);
                          alert('Failed to upload image. Please try again.');
                        } finally {
                          setUploadingImage(false);
                          e.target.value = ''; // Reset input for next upload
                        }
                      }}
                    />
                  </label>}
                </div>
              </div>

              {/* Deal Details */}
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Deal Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Property Type</p>
                    <p className="font-medium">{getPropertyTypeLabel(deal.propertyType)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Acquisition Date</p>
                    <p className="font-medium">
                      {deal.acquisitionDate ? formatDate(deal.acquisitionDate) : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{deal.city}, {deal.state}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className="font-medium">{deal.isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
                {deal.description && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-gray-700">{deal.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Google Map */}
        <div className="lg:col-span-2">
          {deal.address && deal.city && deal.state ? (
            <div className="rounded-lg overflow-hidden h-full border border-gray-200" style={{ minHeight: '340px' }}>
              <iframe
                src={`https://www.google.com/maps?q=${encodeURIComponent(`${deal.address}, ${deal.city}, ${deal.state} ${deal.zipCode}`)}&z=15&output=embed&iwloc=near`}
                className="w-full h-full border-0"
                style={{ minHeight: '340px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Property Location"
              />
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 flex flex-col items-center justify-center h-full" style={{ minHeight: '340px' }}>
              <MapPinIcon className="h-10 w-10 text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">No address set</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'investors', 'distributions', 'documents', 'share-classes', 'waterfall', 'fund-terms'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'share-classes' ? 'Share Classes' : tab === 'fund-terms' ? 'Deal Terms' : tab === 'waterfall' ? 'Waterfall' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'investors' && ` (${deal.investments.length})`}
              {tab === 'distributions' && ` (${deal.distributions.length})`}
              {tab === 'documents' && ` (${deal.documents.length})`}
              {tab === 'share-classes' && ` (${shareClasses.length})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'overview' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {deal.distributions.filter((d: any) => d.status === 'PUBLISHED').slice(0, 3).map((dist) => (
                <div key={dist.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Distribution - {getDistributionTypeLabel(dist.type)}</p>
                    <p className="text-sm text-gray-500">{formatDate(dist.distributionDate)}</p>
                  </div>
                  <p className="font-semibold text-green-600">{formatCurrency(dist.totalAmount)}</p>
                </div>
              ))}
              {deal.distributions.filter((d: any) => d.status === 'PUBLISHED').length === 0 && (
                <p className="text-gray-500 text-center py-4">No published distributions yet</p>
              )}
            </div>

            {/* Custom Deal Information */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Deal Information</h3>
                {!editingCustomFields ? (
                  canEditDeal && <button
                    onClick={startEditingCustomFields}
                    className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingCustomFields(false)}
                      className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 border border-gray-300 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveCustomFields}
                      disabled={savingCustomFields}
                      className="text-sm text-white bg-primary-600 hover:bg-primary-700 px-3 py-1 rounded-lg disabled:opacity-50"
                    >
                      {savingCustomFields ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3">Custom key-value pairs visible to investors on their deal page (max 20)</p>

              {editingCustomFields ? (
                <div className="space-y-1">
                  {customFields.map((field, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        setDragFieldIndex(index);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => {
                        setDragFieldIndex(null);
                        setDragOverFieldIndex(null);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverFieldIndex !== index) setDragOverFieldIndex(index);
                      }}
                      onDragLeave={() => {
                        if (dragOverFieldIndex === index) setDragOverFieldIndex(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragFieldIndex !== null && dragFieldIndex !== index) {
                          const updated = [...customFields];
                          const [moved] = updated.splice(dragFieldIndex, 1);
                          updated.splice(index, 0, moved);
                          setCustomFields(updated);
                        }
                        setDragFieldIndex(null);
                        setDragOverFieldIndex(null);
                      }}
                      className={`flex gap-2 items-center rounded-lg transition-all ${
                        dragFieldIndex === index ? 'opacity-40' : ''
                      } ${
                        dragOverFieldIndex === index && dragFieldIndex !== index
                          ? 'border-t-2 border-primary-500 pt-1'
                          : ''
                      }`}
                    >
                      <div className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <Bars2Icon className="h-4 w-4" />
                      </div>
                      <input
                        type="text"
                        value={field.key}
                        onChange={(e) => {
                          const updated = [...customFields];
                          updated[index] = { ...updated[index], key: e.target.value };
                          setCustomFields(updated);
                        }}
                        placeholder="Label (e.g. Property Type)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => {
                          const updated = [...customFields];
                          updated[index] = { ...updated[index], value: e.target.value };
                          setCustomFields(updated);
                        }}
                        placeholder="Value (e.g. Multi-family)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        onClick={() => setCustomFields(customFields.filter((_, i) => i !== index))}
                        className="p-2 text-gray-400 hover:text-red-500 flex-shrink-0"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {customFields.length < 20 && (
                    <button
                      onClick={() => setCustomFields([...customFields, { key: '', value: '' }])}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 mt-2"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Field
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  {(() => {
                    const fields = (deal as any)?.customFields;
                    if (!fields || !Array.isArray(fields) || fields.length === 0) {
                      return <p className="text-gray-400 text-sm italic">No custom fields configured</p>;
                    }
                    return (
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                        {fields.map((f: { key: string; value: string }, i: number) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-500">{f.key}</span>
                            <span className="font-medium text-gray-900">{f.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'investors' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Investors</h3>
              {canEditDeal && (
                <button
                  onClick={() => setShowAddInvestorModal(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Add Investment
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>
                    {shareClasses.length > 0 && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                    )}
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Investment</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ownership</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Distributed</th>
                    {deal.investments.some((inv: any) => inv.draftDistributed > 0) && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-yellow-600 uppercase">Draft Distribution</th>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deal.investments.map((inv) => (
                    <tr key={inv.id} className={`hover:bg-gray-50 ${!inv.isActive ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="px-6 py-4">
                        <Link to={`/admin/investors/${inv.investorId}`} className="hover:text-primary-600">
                          <p className={`font-medium ${inv.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{inv.investorName}</p>
                          <p className="text-sm text-gray-500">{inv.investorEmail}</p>
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-gray-500">{inv.profileName || 'Individual'}</td>
                      {shareClasses.length > 0 && (
                        <td className="px-6 py-4">
                          {inv.shareClassName ? (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                              {inv.shareClassCode || inv.shareClassName}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 text-center">
                        {inv.isActive ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">Deactivated</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="font-medium">{formatCurrency(inv.investmentAmount)}</p>
                        {inv.contributions && inv.contributions.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedContribution(expandedContribution === inv.id ? null : inv.id);
                            }}
                            className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap"
                          >
                            <svg className={`w-3 h-3 transition-transform ${expandedContribution === inv.id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            {inv.contributions.length} contribution{inv.contributions.length !== 1 ? 's' : ''}
                          </button>
                        )}
                        {expandedContribution === inv.id && inv.contributions && (
                          <div className="mt-2 text-left">
                            <table className="w-full text-xs">
                              <tbody className="divide-y divide-gray-100">
                                {[...inv.contributions]
                                  .sort((a, b) => new Date(a.fundingDate).getTime() - new Date(b.fundingDate).getTime())
                                  .map((c, idx) => (
                                    <tr key={c.id}>
                                      <td className="py-1 pr-2 text-gray-400 whitespace-nowrap">{idx === 0 ? 'Initial' : `Call ${idx}`}</td>
                                      <td className="py-1 pr-2 text-right font-medium text-gray-700 whitespace-nowrap">{formatCurrency(c.amount)}</td>
                                      <td className="py-1 text-right text-gray-400 whitespace-nowrap">
                                        {new Date(c.fundingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">{formatPercentage(inv.ownershipPercentage)}</td>
                      <td className="px-6 py-4 text-right text-green-600">{formatCurrency(inv.totalDistributed)}</td>
                      {deal.investments.some((i: any) => i.draftDistributed > 0) && (
                        <td className="px-6 py-4 text-right text-yellow-600 font-medium">
                          {(inv as any).draftDistributed > 0 ? formatCurrency((inv as any).draftDistributed) : '—'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setEditingInvestment(inv)}
                          className="text-primary-600 hover:text-primary-800 text-sm"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {deal.investments.length === 0 && (
                <p className="text-gray-500 text-center py-8">No investors yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'distributions' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Distributions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deal.distributions.map((dist) => (
                    <tr key={dist.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{formatDate(dist.distributionDate)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {getDistributionTypeLabel(dist.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-sm ${
                          dist.status === 'DRAFT'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {dist.status === 'DRAFT' ? 'Draft' : 'Published'}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-right font-semibold ${
                        dist.status === 'DRAFT' ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {formatCurrency(dist.totalAmount)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">{getPaymentMethodLabel(dist.paymentMethod)}</td>
                      <td className="px-6 py-4 text-gray-500 max-w-xs truncate">{dist.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {deal.distributions.length === 0 && (
                <p className="text-gray-500 text-center py-8">No distributions recorded</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Documents</h3>
              <div className="flex items-center gap-3">
                <Link
                  to={`/admin/documents?dealId=${deal.id}`}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  Manage All
                </Link>
                {canUploadDocument && (
                  <button
                    onClick={() => setShowUploadDocumentModal(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-2"
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    Upload Document
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {deal.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{doc.name}</p>
                      <p className="text-sm text-gray-500">
                        {getDocumentTypeLabel(doc.type)} • {formatDate(doc.createdAt)}
                      </p>
                    </div>
                  </div>
                  <span className="text-primary-600 hover:text-primary-800 text-sm font-medium">
                    View
                  </span>
                </div>
              ))}
              {deal.documents.length === 0 && (
                <p className="text-gray-500 text-center py-8">No documents uploaded</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'share-classes' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Share Classes</h3>
                <p className="text-sm text-gray-500">Configure share classes and waterfall distribution tiers</p>
              </div>
              <div className="flex gap-2">
                {canEditDeal && (
                  <button
                    onClick={() => {
                      setEditingShareClass(null);
                      setShowShareClassModal(true);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Share Class
                  </button>
                )}
              </div>
            </div>

            {loadingShareClasses ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : shareClasses.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No Share Classes Configured</h4>
                <p className="text-gray-500 mb-4">Create share classes to define waterfall distribution structures for this deal.</p>
                {canEditDeal && (
                  <button
                    onClick={() => {
                      setEditingShareClass(null);
                      setShowShareClassModal(true);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                  >
                    Create First Share Class
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {shareClasses.map((shareClass) => (
                  <div key={shareClass.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Share Class Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => setExpandedShareClass(expandedShareClass === shareClass.id ? null : shareClass.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedShareClass === shareClass.id ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{shareClass.name}</h4>
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                              {shareClass.code}
                            </span>
                            {shareClass.isDefault && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {shareClass.preferredReturnRate
                              ? `${(shareClass.preferredReturnRate * 100).toFixed(1)}% Preferred Return`
                              : 'No Preferred Return'}
                            {' • '}
                            {shareClass.waterfallTiers?.length || 0} Waterfall Tiers
                            {shareClass.description && ` • ${shareClass.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canEditDeal && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingShareClass(shareClass);
                              setShowShareClassModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteDeal && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModalTarget({ type: 'shareClass', id: shareClass.id, name: shareClass.name });
                              setDeleteModalText('');
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Share Class Details (Expanded) */}
                    {expandedShareClass === shareClass.id && (
                      <div className="p-4 border-t border-gray-200">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-600 font-medium">LP/GP Split</p>
                            <p className="text-lg font-semibold text-blue-900">
                              {(shareClass.lpSplitPercent * 100).toFixed(2)}% / {(shareClass.gpSplitPercent * 100).toFixed(2)}%
                            </p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-green-600 font-medium">Preferred Return</p>
                            <p className="text-lg font-semibold text-green-900">
                              {shareClass.preferredReturnRate
                                ? `${(shareClass.preferredReturnRate * 100).toFixed(1)}%`
                                : 'None'}
                            </p>
                          </div>
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <p className="text-xs text-purple-600 font-medium">Compounding</p>
                            <p className="text-lg font-semibold text-purple-900 capitalize">
                              {shareClass.compoundingMethod.toLowerCase()}
                            </p>
                          </div>
                          <div className="p-3 bg-amber-50 rounded-lg">
                            <p className="text-xs text-amber-600 font-medium">Catch-Up</p>
                            <p className="text-lg font-semibold text-amber-900">
                              {shareClass.catchUpPercent
                                ? `${(shareClass.catchUpPercent * 100).toFixed(0)}%`
                                : 'None'}
                            </p>
                          </div>
                        </div>

                        {/* Waterfall Tiers */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium text-gray-900">Waterfall Tiers</h5>
                            <div className="flex gap-2">
                              {(!shareClass.waterfallTiers || shareClass.waterfallTiers.length === 0) && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await adminService.createDefaultTiers(shareClass.id);
                                      await loadShareClasses();
                                    } catch (err) {
                                      alert(getApiError(err).message);
                                    }
                                  }}
                                  className="text-sm text-primary-600 hover:text-primary-800"
                                >
                                  Create Default Tiers
                                </button>
                              )}
                            </div>
                          </div>

                          {shareClass.waterfallTiers && shareClass.waterfallTiers.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tier Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hurdle Type</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hurdle Value</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">LP Split</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">GP Split</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">GP Promote</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {shareClass.waterfallTiers
                                    .sort((a, b) => a.tierNumber - b.tierNumber)
                                    .map((tier) => (
                                      <tr key={tier.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm text-gray-900">{tier.tierNumber}</td>
                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{tier.name}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                          <span className={`px-2 py-0.5 text-xs rounded ${
                                            tier.hurdleType === 'IRR' ? 'bg-blue-100 text-blue-800' :
                                            tier.hurdleType === 'CASH_ON_CASH' ? 'bg-green-100 text-green-800' :
                                            'bg-purple-100 text-purple-800'
                                          }`}>
                                            {tier.hurdleType === 'IRR' ? 'IRR' :
                                             tier.hurdleType === 'CASH_ON_CASH' ? 'Cash-on-Cash' :
                                             'Equity Multiple'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                                          {tier.hurdleValue != null
                                            ? tier.hurdleType === 'EQUITY_MULTIPLE'
                                              ? `${tier.hurdleValue.toFixed(2)}x`
                                              : `${(tier.hurdleValue * 100).toFixed(1)}%`
                                            : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                                          {(tier.lpSplitPercent * 100).toFixed(2)}%
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                                          {(tier.gpSplitPercent * 100).toFixed(2)}%
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                                          {tier.gpPromotePercent ? `${(tier.gpPromotePercent * 100).toFixed(2)}%` : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right">
                                          {canDeleteDeal && (
                                            <button
                                              onClick={() => {
                                                setDeleteModalTarget({ type: 'tier', id: tier.id, name: tier.name });
                                                setDeleteModalText('');
                                                setShowDeleteModal(true);
                                              }}
                                              className="text-red-600 hover:text-red-800"
                                            >
                                              <TrashIcon className="h-4 w-4" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-6 bg-gray-50 rounded-lg">
                              <p className="text-gray-500 text-sm">No waterfall tiers configured</p>
                              <p className="text-gray-400 text-xs mt-1">Click "Create Default Tiers" to set up a standard waterfall structure</p>
                            </div>
                          )}

                          {/* Investors in this Share Class */}
                          {deal && (() => {
                            const classInvestors = deal.investments.filter(
                              inv => inv.shareClassId === shareClass.id && inv.isActive
                            );
                            if (classInvestors.length === 0) return null;
                            return (
                              <div className="mt-4">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">
                                  Investors ({classInvestors.length})
                                </h5>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Investor</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Investment</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ownership</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Distributed</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {classInvestors.map(inv => (
                                        <tr key={inv.id} className="hover:bg-gray-50">
                                          <td className="px-4 py-2">
                                            <Link to={`/admin/investors/${inv.investorId}`} className="text-primary-600 hover:text-primary-800">
                                              {inv.investorName}
                                            </Link>
                                            <p className="text-xs text-gray-400">{inv.profileName || 'Individual'}</p>
                                          </td>
                                          <td className="px-4 py-2 text-right font-medium">{formatCurrency(inv.investmentAmount)}</td>
                                          <td className="px-4 py-2 text-right">{formatPercentage(inv.ownershipPercentage)}</td>
                                          <td className="px-4 py-2 text-right text-green-600">{formatCurrency(inv.totalDistributed)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'waterfall' && (
          <div className="p-6">
            <WaterfallTab dealId={deal.id} shareClasses={shareClasses} clawbackEnabled={(deal as any).clawbackEnabled} lookbackEnabled={(deal as any).lookbackEnabled} />
          </div>
        )}

        {activeTab === 'fund-terms' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Deal Terms & Structure</h3>
                <p className="text-sm text-gray-500">Configure PE/RE economic structure, fees, and provisions</p>
              </div>
              <button
                onClick={() => setShowFundTermsModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-2"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Deal Terms
              </button>
            </div>

            {/* Investment Period */}
            <div className="mb-8">
              <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm">1</span>
                Investment Period
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Start Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).investmentPeriodStart ? formatDate((deal as any).investmentPeriodStart) : 'Not set'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">End Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).investmentPeriodEnd ? formatDate((deal as any).investmentPeriodEnd) : 'Not set'}
                  </p>
                </div>
              </div>
            </div>

            {/* Fee Structure */}
            <div className="mb-8">
              <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-sm">2</span>
                Fee Structure
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                {/* Asset Management Fee */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-2">Asset Management Fee</p>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{(deal as any).managementFeeRate != null ? `${((deal as any).managementFeeRate * 100).toFixed(2)}%` : 'Not set'}</span>
                      {(deal as any).managementFeeBasis && (
                        <span className="text-gray-500 ml-1">
                          of {(deal as any).managementFeeBasis.replace(/_/g, ' ').toLowerCase()}
                        </span>
                      )}
                    </p>
                    {(deal as any).managementFeeReducedRate != null && (
                      <p className="text-xs text-gray-500">
                        Reduced rate: {((deal as any).managementFeeReducedRate * 100).toFixed(2)}% (post-investment period)
                      </p>
                    )}
                  </div>
                </div>
                {/* Acquisition Fee */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Acquisition Fee</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).acquisitionFeeRate != null ? `${((deal as any).acquisitionFeeRate * 100).toFixed(2)}%` : 'Not set'}
                  </p>
                </div>
                {/* Disposition Fee */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Disposition Fee</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).dispositionFeeRate != null ? `${((deal as any).dispositionFeeRate * 100).toFixed(2)}%` : 'Not set'}
                  </p>
                </div>
                {/* Asset Management Fee */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Asset Management Fee</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).assetManagementFeeRate != null ? `${((deal as any).assetManagementFeeRate * 100).toFixed(2)}%` : 'Not set'}
                  </p>
                </div>
                {/* Fee Offset */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Fee Offset</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).feeOffsetEnabled
                      ? `${((deal as any).feeOffsetPercent * 100).toFixed(0)}% offset enabled`
                      : 'Disabled'}
                  </p>
                </div>
                {/* Organizational Expenses */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Organizational Expenses</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).orgExpensesCap
                      ? `Cap: ${formatCurrency((deal as any).orgExpensesCap)}${(deal as any).orgExpensesAmortized ? ' (amortized)' : ''}`
                      : 'No cap set'}
                  </p>
                </div>
              </div>
            </div>

            {/* Capital Management */}
            <div className="mb-8">
              <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-sm">3</span>
                Capital Management
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-10">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Total Committed Capital</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {(deal as any).totalCommittedCapital ? formatCurrency((deal as any).totalCommittedCapital) : 'Not set'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Total Called Capital</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {(deal as any).totalCalledCapital ? formatCurrency((deal as any).totalCalledCapital) : '$0'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Unfunded Commitments</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {(deal as any).totalCommittedCapital && (deal as any).totalCalledCapital
                      ? formatCurrency((deal as any).totalCommittedCapital - (deal as any).totalCalledCapital)
                      : (deal as any).totalCommittedCapital
                        ? formatCurrency((deal as any).totalCommittedCapital)
                        : 'N/A'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">GP Pari Passu</p>
                  {shareClasses.length > 0 ? (
                    shareClasses.every(sc => sc.preferredReturnType === shareClasses[0].preferredReturnType) ? (
                      <>
                        <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <span className={`inline-block w-3 h-3 rounded-full ${shareClasses[0].preferredReturnType === 'PARI_PASSU' ? 'bg-green-500' : 'bg-gray-300'}`} />
                          {shareClasses[0].preferredReturnType === 'PARI_PASSU' ? 'Enabled' : 'Disabled (True Preferred)'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">(see Share Classes to edit)</p>
                      </>
                    ) : (
                      <>
                        <div className="space-y-1">
                          {shareClasses.map(sc => (
                            <p key={sc.id} className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              <span className={`inline-block w-3 h-3 rounded-full ${sc.preferredReturnType === 'PARI_PASSU' ? 'bg-green-500' : 'bg-gray-300'}`} />
                              {sc.name}: {sc.preferredReturnType === 'PARI_PASSU' ? 'Pari Passu' : 'True Preferred'}
                            </p>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">(see Share Classes to edit)</p>
                      </>
                    )
                  ) : (
                    <p className="text-sm text-gray-400">No share classes</p>
                  )}
                </div>
                <div className="p-4 bg-gray-50 rounded-lg col-span-full md:col-span-1">
                  <p className="text-xs text-gray-500 font-medium mb-1">Capital Recycling</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).recyclingEnabled
                      ? <>
                          Enabled
                          {(deal as any).recyclingLimit && (
                            <span className="text-gray-500 ml-1">
                              (up to {((deal as any).recyclingLimit * 100).toFixed(0)}% of commitments)
                            </span>
                          )}
                          {(deal as any).recyclingPeriodEnd && (
                            <span className="text-gray-500 block text-xs mt-1">
                              Until {formatDate((deal as any).recyclingPeriodEnd)}
                            </span>
                          )}
                        </>
                      : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>

            {/* Promoted Interest */}
            <div className="mb-8">
              <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 text-sm">4</span>
                Promoted Interest
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                {/* Promote Recipient Entity */}
                <div className="p-4 bg-gray-50 rounded-lg col-span-full">
                  <p className="text-xs text-gray-500 font-medium mb-2">Promoted Interest Entity</p>
                  {(() => {
                    const currentRecipient = deal.investments.find((inv: any) => inv.isPromoteRecipient);
                    const currentLabel = currentRecipient ? `${currentRecipient.investorName} - ${currentRecipient.profileName}` : '';
                    const filtered = deal.investments.filter((inv: any) => {
                      if (!promoteSearch) return true;
                      const label = `${inv.investorName} - ${inv.profileName}`.toLowerCase();
                      return label.includes(promoteSearch.toLowerCase());
                    });
                    return (
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search entity..."
                          value={promoteDropdownOpen ? promoteSearch : currentLabel}
                          onChange={(e) => {
                            setPromoteSearch(e.target.value);
                            setPromoteDropdownOpen(true);
                          }}
                          onFocus={() => {
                            setPromoteSearch('');
                            setPromoteDropdownOpen(true);
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setPromoteDropdownOpen(false);
                              setPromoteSearch('');
                            }, 200);
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        {promoteDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filtered.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-400">No matching entities</div>
                            ) : (
                              filtered.map((inv: any) => (
                                <button
                                  key={inv.id}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 ${
                                    inv.isPromoteRecipient ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                                  }`}
                                  onMouseDown={async (e) => {
                                    e.preventDefault();
                                    try {
                                      // Close dropdown and show selected entity immediately
                                      setPromoteDropdownOpen(false);
                                      setPromoteSearch('');
                                      await adminService.updateInvestment(inv.id, {
                                        investmentAmount: inv.investmentAmount,
                                        isActive: inv.isActive,
                                        isPromoteRecipient: true,
                                      });
                                      // Silent refresh - don't show loading spinner or reset scroll
                                      await loadDeal(true);
                                    } catch (err) {
                                      console.error('Failed to update promote recipient:', err);
                                    }
                                  }}
                                >
                                  {inv.investorName} - {inv.profileName}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-xs text-gray-400 mt-1">
                    This entity receives GP carried interest / promoted interest from waterfall distributions.
                  </p>
                </div>

                {/* Clawback Provision */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Clawback Provision</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).clawbackEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                  {(deal as any).clawbackEnabled && (
                    <p className="text-xs text-gray-500 mt-1">
                      GP must return excess carried interest if fund underperforms
                    </p>
                  )}
                </div>

                {/* Lookback Provision */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Lookback Provision</p>
                  <p className="text-sm font-medium text-gray-900">
                    {(deal as any).lookbackEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                  {(deal as any).lookbackEnabled && (
                    <p className="text-xs text-gray-500 mt-1">
                      Distributions evaluated across fund lifecycle at liquidation
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Key Person Provisions */}
            <div className="mb-8">
              <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-sm">5</span>
                Key Person Provisions
              </h4>
              <div className="pl-10">
                {(deal as any).keyPersonsEnabled ? (
                  <div className={`p-4 rounded-lg ${(deal as any).keyPersonTriggered ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                    {(deal as any).keyPersonTriggered ? (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-red-800">Key Person Event Triggered</p>
                          <p className="text-xs text-red-600 mt-1">
                            Triggered on {(deal as any).keyPersonTriggerDate ? formatDate((deal as any).keyPersonTriggerDate) : 'N/A'}
                          </p>
                          {(deal as any).keyPersonNames && (deal as any).keyPersonNames.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                              Key Persons: {(deal as any).keyPersonNames.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-900">Key Person Provision Active</p>
                        {(deal as any).keyPersonNames && (deal as any).keyPersonNames.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Key Persons: {(deal as any).keyPersonNames.join(', ')}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Investment period may be suspended if key persons depart
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">No key person provision configured</p>
                  </div>
                )}
              </div>
            </div>

                      </div>
        )}
      </div>

      {/* Edit Deal Terms Modal */}
      {showFundTermsModal && deal && (
        <EditFundTermsModal
          isOpen={showFundTermsModal}
          deal={deal}
          onClose={() => setShowFundTermsModal(false)}
          onSaved={() => loadDeal(true)}
        />
      )}

      {/* Add Investment Modal */}
      {showAddInvestorModal && deal && (
        <AddInvestmentModal
          dealId={deal.id}
          dealName={deal.name}
          totalEquityRaised={deal.totalEquityRaised}
          shareClasses={shareClasses}
          onClose={() => setShowAddInvestorModal(false)}
          onCreated={() => loadDeal(true)}
        />
      )}

      {/* Add Distribution Modal */}
      {showAddDistributionModal && deal && (
        <AddDistributionModal
          dealId={deal.id}
          shareClasses={shareClasses}
          onClose={() => setShowAddDistributionModal(false)}
          onCreated={() => loadDeal(true)}
        />
      )}

      {/* Edit Distribution Modal */}
      {editingDistribution && (
        <EditDistributionModal
          distribution={editingDistribution}
          existingShareClasses={shareClasses}
          onClose={() => setEditingDistribution(null)}
          onSaved={() => loadDeal(true)}
        />
      )}

      {/* Edit Investment Modal */}
      {editingInvestment && deal && (
        <EditInvestmentModal
          investment={editingInvestment}
          shareClasses={shareClasses}
          onClose={() => setEditingInvestment(null)}
          onSaved={() => loadDeal(true)}
        />
      )}

      {/* Edit Deal Modal */}
      {deal && (
        <EditDealModal
          isOpen={showEditModal}
          deal={deal}
          onClose={() => setShowEditModal(false)}
          onSaved={() => loadDeal(true)}
        />
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          getEmbedUrls={adminService.getDocumentViewUrl}
        />
      )}

      {/* Share Class Modal */}
      {showShareClassModal && deal && (
        <ShareClassModal
          dealId={deal.id}
          shareClass={editingShareClass}
          onClose={() => {
            setShowShareClassModal(false);
            setEditingShareClass(null);
          }}
          onSaved={loadShareClasses}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Deal</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{deal.name}</strong>?
              </p>
              <p className="mt-2 text-sm text-gray-500">
                This will permanently delete the deal and all associated:
              </p>
              <ul className="mt-1 text-sm text-gray-500 list-disc list-inside">
                <li>{deal.investments?.length || 0} investment(s)</li>
                <li>{deal.distributions?.length || 0} distribution(s)</li>
                <li>{deal.documents?.length || 0} document(s)</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDeal}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    Delete Deal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Class / Tier Delete Confirmation Modal */}
      {showDeleteModal && deleteModalTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete {deleteModalTarget.type === 'shareClass' ? 'Share Class' : 'Waterfall Tier'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {deleteModalTarget.name}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteModalText}
                onChange={(e) => setDeleteModalText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteModalTarget(null);
                  setDeleteModalText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={deleteModalLoading}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteModalText.toLowerCase() !== 'proceed' || !deleteModalTarget) return;
                  try {
                    setDeleteModalLoading(true);
                    if (deleteModalTarget.type === 'shareClass') {
                      await adminService.deleteShareClass(deleteModalTarget.id);
                    } else if (deleteModalTarget.type === 'tier') {
                      await adminService.deleteWaterfallTier(deleteModalTarget.id);
                    }
                    await loadShareClasses();
                    setShowDeleteModal(false);
                    setDeleteModalTarget(null);
                    setDeleteModalText('');
                  } catch (err) {
                    console.error('Delete failed:', err);
                  } finally {
                    setDeleteModalLoading(false);
                  }
                }}
                disabled={deleteModalText.toLowerCase() !== 'proceed' || deleteModalLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleteModalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  `Delete ${deleteModalTarget.type === 'shareClass' ? 'Share Class' : 'Tier'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadDocumentModal && deal && (
        <UploadDocumentModal
          isOpen={showUploadDocumentModal}
          dealId={deal.id}
          dealName={deal.name}
          onClose={() => setShowUploadDocumentModal(false)}
          onUploaded={async () => {
            await loadDeal(true);
            setShowUploadDocumentModal(false);
          }}
        />
      )}
    </div>
  );
}

export default DealDetailPage;
